/**
 * O motor do tour guiado: o destaque, o balão e o teclado.
 *
 * ## Como ele respeita a CSP
 *
 * A aplicação roda sob `style-src 'self'`, sem `unsafe-inline`. Medido no
 * navegador em 21/10: o **atributo** `style=` e o elemento `<style>` são
 * recusados; atribuição por **CSSOM** passa.
 *
 * Então nada aqui escreve estilo. O que atravessa a fronteira são quatro
 * números, como propriedades customizadas, e a folha estática
 * (`styles/catalog.css`) lê cada uma. Cor, sombra, animação e modo escuro
 * ficam lá — este arquivo não tem opinião visual.
 *
 * ## O destaque é uma sombra, não um buraco
 *
 * Recortar o fundo pediria SVG com máscara ou quatro divs. Uma sombra enorme
 * (`box-shadow: 0 0 0 9999px`) num retângulo posicionado faz o mesmo efeito com
 * um elemento só, e **deixa o alvo clicável**, porque a sombra não captura
 * ponteiro. Importa: num tour sobre uma plataforma, poder clicar no que está
 * sendo explicado é metade do aprendizado.
 *
 * ## Sair não é dispensar
 *
 * `Esc`, o × e *Sair do tour* gravam `visto`. Só o × do **convite** grava
 * `dispensado`. Quem saiu no passo 2 não perdeu o tour — ele continua no menu,
 * e o convite ainda pode voltar numa próxima visita.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api.js';
import { calcular, retanguloDe } from './posicao.js';
import { passosDe, tourPorId, TOURS } from './passos.js';

const TourContext = createContext(null);

/** Quem quiser abrir um tour de qualquer tela. */
export const useTour = () => useContext(TourContext) ?? { abrir: () => {}, disponiveis: () => [] };

/** O elemento de uma âncora, ou nulo. */
const acharAlvo = (nome) =>
  nome ? document.querySelector(`[data-tour="${CSS.escape(nome)}"]`) : null;

/**
 * Grava o progresso sem nunca derrubar a tela.
 *
 * Falhar aqui significa, no pior caso, o convite voltar numa próxima visita.
 * Derrubar o tour por causa disso seria trocar um incômodo por um defeito.
 *
 * ## ⚠ Mas o silêncio não pode ser TOTAL, e isso custou caro
 *
 * A primeira versão engolia a falha sem dizer nada. Em 22/10 descobriu-se que
 * **todas** as gravações respondiam 400 — `apiFetch` não mandava
 * `content-type` e o corpo nunca era analisado. O tour parecia funcionar
 * perfeitamente; só não lembrava de nada.
 *
 * Quem achou foi o usuário, com o console aberto. Nenhum teste pegaria: eles
 * montam a requisição com o cabeçalho certo, exercitando um caminho que o
 * aplicativo não tomava.
 *
 * O `console.warn` é o que faz a diferença entre "não derruba a tela" e "não
 * deixa rastro". Engolir exceção é decisão legítima; engoli-la **sem registro**
 * é esconder defeito de quem poderia consertá-lo.
 */
async function registrar(tourId, situacao, versao) {
  try {
    await apiFetch(`/api/me/tour/${encodeURIComponent(tourId)}`, {
      method: 'POST',
      body: JSON.stringify({ situacao, versao }),
    });
  } catch (err) {
    console.warn(`tour: nao consegui gravar "${situacao}" de ${tourId}`, err?.message ?? err);
  }
}

/* ── o balão ─────────────────────────────────────────────────────────────── */

function Balao({ passo, indice, total, onAnterior, onProximo, onSair }) {
  const [caixa, setCaixa] = useState(null);
  const navigate = useNavigate();

  // ## O passo que muda de tela
  //
  // O tour de administração atravessa as oito abas. Navegar ANTES de medir não
  // é detalhe: a âncora do passo só existe depois que a aba trocou, e medir
  // antes acharia nada e centralizaria o balão — com o texto certo, no lugar
  // errado, sem nada falhar.
  useEffect(() => {
    if (!passo.rota) return;
    const agora = `${window.location.pathname}${window.location.search}`;
    if (agora !== passo.rota) navigate(passo.rota, { replace: true });
  }, [passo, navigate]);

  // ## ⚠ Por que isto PROCURA a âncora em vez de esperar um prazo
  //
  // A primeira versão media depois de 320 ms, e de novo aos 700 ms. Funcionava
  // quase sempre e falhava exatamente onde doía: no passo do sino, que vem
  // logo depois de uma troca de rota. Cada página monta a própria moldura, de
  // modo que navegar **destrói e recria** o cabeçalho — e a âncora do sino
  // ainda não existia quando os dois prazos venceram.
  //
  // O sintoma, relatado pelo usuário em 22/10: o balão nascia no meio da tela
  // e só ia para junto do sino quando alguém rolava a página — porque o
  // ouvinte de rolagem remediava o que a medição tinha perdido.
  //
  // Prazo fixo é aposta na velocidade da máquina de quem olha. Procurar quadro
  // a quadro não é: acha assim que a âncora existe, em qualquer máquina, e
  // desiste quando o passo de fato não tem alvo.
  useEffect(() => {
    // Recomeça a cada passo. Sem isto o balão aparece por um instante no lugar
    // do passo anterior — e a animação de entrada, que depende da classe sair
    // e voltar, rodaria uma vez no tour inteiro em vez de uma por passo.
    setCaixa(null);

    let vivo = true;
    let tentativas = 0;
    let jaRolou = false;
    let quadro = null;
    let depoisDaRolagem = null;

    const posicionar = () => {
      if (!vivo) return;
      const balao = document.getElementById('tour-balao');
      if (!balao) return;

      const elemento = acharAlvo(passo.alvo);
      const alvo = retanguloDe(elemento);

      // Ainda não montou. Passo COM âncora insiste por ~2 s; passo sem âncora
      // não tem o que esperar e desenha centralizado já.
      if (passo.alvo && !alvo && tentativas < 120) {
        tentativas += 1;
        quadro = requestAnimationFrame(posicionar);
        return;
      }

      // Rola até o alvo UMA vez, quando ele aparece. Rolar a cada medição
      // brigaria com quem está lendo e quer olhar outra coisa da tela.
      if (alvo && !jaRolou) {
        jaRolou = true;
        elemento?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // A rolagem muda o retângulo; remede quando ela terminar.
        depoisDaRolagem = setTimeout(posicionar, 420);
      }

      const janela = { largura: window.innerWidth, altura: window.innerHeight };
      const tamanho = { largura: balao.offsetWidth, altura: balao.offsetHeight };
      const { x, y, lado } = calcular(alvo, tamanho, janela, passo.lado);

      // CSSOM: passa pela CSP. Só número atravessa a fronteira; a folha
      // estática decide tudo o que é visual.
      const raiz = document.documentElement;
      raiz.style.setProperty('--tour-x', `${Math.round(x)}px`);
      raiz.style.setProperty('--tour-y', `${Math.round(y)}px`);
      raiz.style.setProperty('--tour-alvo-x', `${Math.round(alvo?.left ?? 0)}px`);
      raiz.style.setProperty('--tour-alvo-y', `${Math.round(alvo?.top ?? 0)}px`);
      raiz.style.setProperty('--tour-alvo-l', `${Math.round(alvo?.largura ?? 0)}px`);
      raiz.style.setProperty('--tour-alvo-a', `${Math.round(alvo?.altura ?? 0)}px`);

      setCaixa({ lado, temAlvo: Boolean(alvo) });
    };

    quadro = requestAnimationFrame(posicionar);

    window.addEventListener('resize', posicionar);
    window.addEventListener('scroll', posicionar, true);

    return () => {
      vivo = false;
      if (quadro) cancelAnimationFrame(quadro);
      if (depoisDaRolagem) clearTimeout(depoisDaRolagem);
      window.removeEventListener('resize', posicionar);
      window.removeEventListener('scroll', posicionar, true);
    };
  }, [passo]);

  const ultimo = indice === total - 1;

  return (
    <>
      {/* O destaque. Sem alvo ele não existe — passo centralizado fala da
          plataforma, não de um canto dela. */}
      {caixa?.temAlvo && <div className="tour-destaque" aria-hidden="true" />}

      <div
        id="tour-balao"
        className={`tour-balao ${caixa ? `is-${caixa.lado}` : 'is-medindo'}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-titulo"
      >
        {/* Aponta para o que o balão explica. Desenhada pelo CSS a partir da
            classe do lado, que o motor já calculou — aqui é só o nó. */}
        <span className="tour-seta" aria-hidden="true" />

        <p className="tour-contagem">
          {indice + 1} de {total}
        </p>
        <h2 id="tour-titulo" className="tour-titulo">
          {passo.titulo}
        </h2>
        <p className="tour-texto">{passo.texto}</p>

        <div className="tour-acoes">
          <button type="button" className="button ghost" onClick={onSair}>
            Sair do tour
          </button>
          <span className="tour-espaco" />
          {indice > 0 && (
            <button type="button" className="button secondary" onClick={onAnterior}>
              Voltar
            </button>
          )}
          <button type="button" className="button primary" onClick={onProximo}>
            {ultimo ? 'Concluir' : 'Próximo'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ── o convite ───────────────────────────────────────────────────────────── */

/**
 * O convite do primeiro acesso.
 *
 * ## Por que convite e não o tour direto
 *
 * A pessoa acabou de atravessar um login corporativo e chegou numa tela que
 * não conhece. Abrir oito balões por cima disso é o comportamento que faz
 * alguém clicar no × antes de ler. O convite é uma linha, e o × dela é a única
 * coisa nesta feature que grava `dispensado`.
 */
function Convite({ tour, onAceitar, onDispensar }) {
  return (
    <div className="tour-convite" role="region" aria-label="Convite para o tour guiado">
      <div className="tour-convite-texto">
        <strong>Primeira vez por aqui?</strong>
        <span>{tour.resumo}</span>
      </div>
      <button type="button" className="button primary" onClick={onAceitar}>
        Fazer o tour
      </button>
      <button
        type="button"
        className="close-button"
        onClick={onDispensar}
        aria-label="Dispensar o convite"
      >
        ×
      </button>
    </div>
  );
}

/* ── o provedor ──────────────────────────────────────────────────────────── */

export function TourProvider({ children }) {
  const navigate = useNavigate();

  const [ativo, setAtivo] = useState(null); // { tour, passos, indice }
  const [convite, setConvite] = useState(null);

  // O que oferecer. Falhar aqui é não oferecer nada — nunca um erro na tela.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await apiFetch('/api/me/tour');
        if (!vivo || !r.primeiraVisita) return;

        // O convite é sempre o tour de boas-vindas. Administrador também
        // começa por ele — conhecer a plataforma vem antes de administrá-la.
        const tour = TOURS[0];
        const feito = r.progresso?.[tour.id];
        // Dispensou, concluiu, ou já viu esta versão: nada a oferecer.
        if (feito?.dispensado || feito?.concluido || feito?.versao >= tour.versao) return;

        setConvite(tour);
      } catch {
        /* sem convite, e sem barulho */
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  const sair = useCallback(
    (comoTerminou) => {
      setAtivo((atual) => {
        if (atual) registrar(atual.tour.id, comoTerminou, atual.tour.versao);
        return null;
      });
      // As propriedades ficariam penduradas na raiz depois de fechar.
      for (const nome of ['x', 'y', 'alvo-x', 'alvo-y', 'alvo-l', 'alvo-a']) {
        document.documentElement.style.removeProperty(`--tour-${nome}`);
      }
    },
    [setAtivo],
  );

  const abrir = useCallback(
    (id) => {
      const tour = tourPorId(id);
      if (!tour) return;

      setConvite(null);

      const agora = `${window.location.pathname}${window.location.search}`;
      const precisaIr = Boolean(tour.rota) && agora !== tour.rota;
      if (precisaIr) navigate(tour.rota);

      // ## Por que o contexto é lido DEPOIS de navegar
      //
      // `quando` decide quais passos fazem sentido, e a resposta vem do DOM —
      // tem cartão na tela? tem artefato publicado? Lendo antes da navegação, o
      // tour de artefatos aberto a partir de Agentes não acharia cartão nenhum
      // e **perderia quatro dos sete passos em silêncio**, porque a tela certa
      // ainda não tinha montado.
      //
      // O atraso é visível (a tela troca, o balão aparece em seguida) e é o
      // preço certo: um tour que começa meio segundo depois é melhor que um
      // tour que ensina metade.
      const comecar = () => {
        const contexto = {
          temCartao: Boolean(acharAlvo('catalogo-cartao')),
          temItemParaSolicitar: Boolean(document.querySelector('[data-tour-solicitavel="sim"]')),
          temArtefato: Boolean(acharAlvo('artefatos-cartao')),
        };

        // O filtro roda UMA vez. Refazê-lo a cada passo faria a contagem
        // "3 de 7" mudar embaixo de quem está lendo.
        const passos = passosDe(tour, contexto);
        if (passos.length === 0) return;

        registrar(tour.id, 'visto', tour.versao);
        setAtivo({ tour, passos, indice: 0 });
      };

      if (precisaIr) setTimeout(comecar, 400);
      else comecar();
    },
    [navigate],
  );

  const andar = useCallback(
    (delta) => {
      setAtivo((atual) => {
        if (!atual) return atual;
        const proximo = atual.indice + delta;
        if (proximo < 0) return atual;
        if (proximo >= atual.passos.length) {
          registrar(atual.tour.id, 'concluido', atual.tour.versao);
          return null;
        }
        return { ...atual, indice: proximo };
      });
    },
    [setAtivo],
  );

  // `Esc` fecha, como em todo diálogo desta aplicação.
  useEffect(() => {
    if (!ativo) return undefined;
    const aoTeclar = (e) => {
      if (e.key === 'Escape') sair('visto');
      if (e.key === 'ArrowRight') andar(1);
      if (e.key === 'ArrowLeft') andar(-1);
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [ativo, sair, andar]);

  // `disponiveis` é filtrado por papel: oferecer o tour de administração a
  // quem não é administrador seria prometer telas que a pessoa não abre.
  const valor = useMemo(
    () => ({
      abrir,
      disponiveis: (ehAdmin) =>
        TOURS.filter((t) => !t.contextual && (t.papel !== 'admin' || ehAdmin)),
    }),
    [abrir],
  );

  return (
    <TourContext.Provider value={valor}>
      {children}

      {convite && !ativo && (
        <Convite
          tour={convite}
          onAceitar={() => abrir(convite.id)}
          onDispensar={() => {
            registrar(convite.id, 'dispensado', convite.versao);
            setConvite(null);
          }}
        />
      )}

      {ativo && (
        <Balao
          passo={ativo.passos[ativo.indice]}
          indice={ativo.indice}
          total={ativo.passos.length}
          onAnterior={() => andar(-1)}
          onProximo={() => andar(1)}
          onSair={() => sair('visto')}
        />
      )}
    </TourContext.Provider>
  );
}

export default TourProvider;
